import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, Check, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const url = filter === 'unread' ? `${API}/notifications?unread_only=true` : `${API}/notifications`;
      const response = await axios.get(url);
      setNotifications(response.data);
    } catch (error) {
      toast.error('فشل تحميل الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('فشل تحديث الإشعار');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'تحديث': Clock,
      'جلسة جديدة': Bell,
      'تغيير حالة': Bell,
    };
    const Icon = icons[type] || Bell;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">الإشعارات</h1>
          <p className="text-gray-500 mt-1">تابع جميع التحديثات والتغييرات</p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="filter-all"
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            الكل
          </Button>
          <Button
            data-testid="filter-unread"
            variant={filter === 'unread' ? 'default' : 'outline'}
            onClick={() => setFilter('unread')}
            className={filter === 'unread' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            غير مقروءة
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>سجل الإشعارات</CardTitle>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد إشعارات</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  data-testid={`notification-${notification.id}`}
                  className={`border rounded-lg p-4 transition-all ${
                    notification.read ? 'bg-white' : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      notification.read ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-600'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <Badge variant="outline" className="mb-2">
                            {notification.type}
                          </Badge>
                          <p className={`text-base ${
                            notification.read ? 'text-gray-700' : 'font-medium text-gray-900'
                          }`}>
                            {notification.message}
                          </p>
                          <p className="text-sm text-gray-500 mt-2">
                            {new Date(notification.sent_at).toLocaleString('ar-KW')}
                          </p>
                        </div>
                        {!notification.read && (
                          <Button
                            data-testid={`mark-read-${notification.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                            className="gap-2 hover:bg-blue-100"
                          >
                            <Check className="w-4 h-4" />
                            وضع علامة كمقروء
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}